import React from 'react';
import dayjs from 'dayjs';

/**
 * Special voucher layout for company 691eb415ec693d7f61130d37
 * Creates a 3-column bank voucher format similar to university fee vouchers
 *
 * Note: Pay / Split / Discount controls live on the parent page (e.g. Voucher.js)
 * above this component — they are not rendered inside BankVoucherLayout.
 *
 * @param {Object} props
 * @param {string} props.company_id - Company ID to check
 * @param {Array} props.bankAccounts - Array of bank account objects
 * @param {Object} props.voucherData - Voucher/Invoice data
 * @param {Object} props.companyData - Company details
 * @param {Object} props.memberData - Member details  
 * @param {React.ReactNode} props.children - Default layout to render if not special company
 */
const BankVoucherLayout = ({
    company_id,
    bankAccounts = [],
    voucherData = {},
    companyData = {},
    memberData = {},
    children
}) => {
    const SPECIAL_COMPANY_ID = '691eb415ec693d7f61130d37';
    const DEMO_COMPANY_ID = '692df1295ea433cd4afbf39a';
    const NEW_COMPANY_ID = '69a14d9b922f2bc2814ef5cf';
    const KUICK_PAY_PREFIX = '08520';

    const normalizedCompanyId = String(company_id?._id ?? company_id ?? '');

    // If not the special company, render default layout
    if (
        normalizedCompanyId !== SPECIAL_COMPANY_ID &&
        normalizedCompanyId !== DEMO_COMPANY_ID &&
        normalizedCompanyId !== NEW_COMPANY_ID
    ) {
        return <>{children}</>;
    }

    // Hardcoded bank accounts for THE UNIVERSITY OF LAHORE as specifically requested
    const hardcodedBankAccounts = [
        {
            bank_name: 'ALLIED BANK LTD',
            details: 'PKR1674300015322 (Bill Type 0125)'
        },
        {
            bank_name: 'BANK AL-HABIB',
            details: '(API Posting Only - 104 UOL)'
        },
        {
            bank_name: 'BANK ISLAMI PAKISTAN LTD',
            details: '(LINKISLAMI)'
        }
    ];

    // Format number to international format (e.g., 1,234.56)
    const formatToInternational = (val) => {
        const num = Number(val) || 0;
        return num.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Extract invoice data if nested (based on the JSON provided by user)
    const invoiceDetails = voucherData.invoice || {};

    // Generate Kuickpay Reference No by concatenating prefix with Bill No
    const billNo = voucherData.invoice_no || invoiceDetails.invoice_no || voucherData.transaction_number || '000000';
    const kuickpayReference = `${KUICK_PAY_PREFIX}${billNo}`;

    // Improved isPaid detection
    const isPaid =
        voucherData.is_paid === true ||
        invoiceDetails.is_paid === true ||
        ['paid', 'Paid', 'PAID'].includes(voucherData.payment_status) ||
        ['paid', 'Paid', 'PAID'].includes(invoiceDetails.payment_status) ||
        (voucherData.voucher_type === 'RECEIPT') || // Receipts are usually paid
        (Number(voucherData.paid_amount) > 0 && Number(voucherData.paid_amount) >= Number(voucherData.total_amount));

    // Force label to "VOUCHER" for specific types as requested
    const displayVoucherType = isPaid ? 'PAID RECEIPT' :
        ((voucherData.voucher_type === 'POS INVOICE' || voucherData.voucher_type === 'RECEIPT')
            ? 'FEE VOUCHER'
            : (voucherData.voucher_type || 'FEE VOUCHER'));

    // Use total_amount and pending_amount from invoice instead of calculating
    const totalFee = Number(invoiceDetails.total_amount || voucherData.total_amount || 0);
    const discount = Number(invoiceDetails.discount || voucherData.discount || 0);
    // For unpaid invoices, show pending amount; for paid, show 0 or the paid amount
    const grandTotal = isPaid
        ? Number(invoiceDetails.pending_amount || voucherData.pending_amount || 0)
        : Number(invoiceDetails.pending_amount || voucherData.pending_amount || totalFee - discount);

    // Render a single voucher column
    const VoucherColumn = ({ copyType, showBorder = true }) => (
        <div style={{
            borderRight: showBorder ? '2px dashed black' : 'none',
            paddingRight: '1.5%',
            paddingLeft: '1.5%',
            paddingTop: '10px',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            boxSizing: 'border-box',
            fontFamily: '"Times New Roman", Times, serif',
            position: 'relative', // Added for stamp positioning
            overflow: 'hidden'    // Contain the stamp
        }}>
            {/* PROMINENT STAMP OVERLAY */}
            {isPaid && (
                <div style={{
                    position: 'absolute',
                    top: '65%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-20deg)',
                    border: '8px double #dc2626', // Double border for more stamp-like feel
                    borderRadius: '12px',
                    padding: '10px 30px',
                    color: '#dc2626',
                    fontSize: '60px',
                    fontWeight: '900',
                    textTransform: 'uppercase',
                    zIndex: 20,
                    pointerEvents: 'none',
                    userSelect: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 0 15px rgba(220, 38, 38, 0.2)',
                    textAlign: 'center',
                    lineHeight: '1',
                    opacity: 0.8
                }}>
                    <div style={{ fontSize: '45px' }}>PAID</div>
                    <div style={{ fontSize: '10px', marginTop: '5px', letterSpacing: '1px' }}>
                        {voucherData.payment_date || invoiceDetails.payment_date ? dayjs(voucherData.payment_date || invoiceDetails.payment_date).format('DD MMM YYYY') : dayjs().format('DD MMM YYYY')}
                    </div>
                </div>
            )}
            {/* Header Section */}
            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <h1 style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    margin: '2px 0',
                    textTransform: 'uppercase'
                }}>
                    THE UNIVERSITY OF LAHORE
                </h1>
                <p style={{ fontSize: '11px', margin: '2px 0', lineHeight: '1.2' }}>
                    Defence Road Campus, 1-km Defence Rd. Near Bhobtian Chowk Raiwind Rd, Lahore, Pakistan.
                </p>
                <p style={{ fontSize: '11px', margin: '2px 0' }}>
                    Ph: 042 111 865 865, Email: fee.admissions@uol.edu.pk
                </p>
                <h3 style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    margin: '5px 0',
                    textDecoration: 'underline'
                }}>
                    {copyType}
                </h3>

                {/* Bank Details */}
                <div style={{ marginTop: '5px', textAlign: 'center' }}>
                    {hardcodedBankAccounts.map((bank, idx) => (
                        <div key={idx} style={{ fontSize: '11px', margin: '1px 0', lineHeight: '1.1' }}>
                            <strong style={{ display: 'block' }}>{bank.bank_name}</strong> {bank.details}
                        </div>
                    ))}
                    <div style={{ fontSize: '11px', margin: '1px 0' }}>
                        <strong>KUICKPAY</strong> Reference No. {kuickpayReference}
                    </div>
                </div>

                <h4 style={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    margin: '10px 0',
                    padding: '5px',
                    textAlign: 'center'
                }}>
                    {displayVoucherType}
                </h4>
            </div>

            {/* Voucher Information Row */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                fontWeight: 'bold',
                marginBottom: '8px',
                borderBottom: '1px solid #eee',
                paddingBottom: '4px'
            }}>
                <span>Voucher No: <u>{voucherData.invoice_no || 'N/A'}</u></span>
                <span>Due Date: <u>{voucherData.due_date ? dayjs(voucherData.due_date).format('DD-MM-YYYY') : 'N/A'}</u></span>
            </div>

            {/* Member Details Section */}
            <div style={{ fontSize: '11px', marginBottom: '10px', flexGrow: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 15px' }}>
                    <p style={{ margin: '1px 0', minWidth: '45%' }}>Voucher Date: <u>{voucherData.voucher_date ? dayjs(voucherData.voucher_date).format('DD-MM-YYYY') : dayjs().format('DD-MM-YYYY')}</u></p>
                    <p style={{ margin: '1px 0', minWidth: '45%' }}>Member ID: <u>{memberData.member_id || 'N/A'}</u></p>
                    <p style={{ margin: '1px 0', width: '100%' }}>Member Name: <u>{memberData.name || 'N/A'}</u></p>
                    <p style={{ margin: '1px 0', minWidth: '45%' }}>Phone: <u>{memberData.phone || 'N/A'}</u></p>
                    <p style={{ margin: '1px 0', minWidth: '45%' }}>Package: <u>{voucherData.package_name || memberData.package_name || 'N/A'}</u></p>
                    {isPaid && (
                        <div style={{
                            width: '100%',
                            backgroundColor: '#ecfdf5',
                            padding: '4px',
                            marginTop: '4px',
                            border: '1px solid #10b981',
                            borderRadius: '4px'
                        }}>
                            <p style={{ margin: '0', fontWeight: 'bold', color: '#047857' }}>
                                Payment Method: {voucherData.payment_method || 'Bank'}
                            </p>
                            <p style={{ margin: '0', fontSize: '9px', color: '#065f46' }}>
                                Reference: {voucherData.reference_number || 'N/A'}
                            </p>
                            <p style={{ margin: '0', fontSize: '9px', color: '#065f46' }}>
                                Payment Date: {voucherData.payment_date || invoiceDetails.payment_date ? dayjs(voucherData.payment_date || invoiceDetails.payment_date).format('DD-MM-YYYY HH:mm') : 'N/A'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Fee Breakdown Table */}
            <div style={{ flexGrow: 1 }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    borderBottom: '2px solid black',
                    paddingBottom: '3px',
                    marginBottom: '5px'
                }}>
                    <span>Description</span>
                    <span>Amount (PKR)</span>
                </div>

                {/* Fees List */}
                {(() => {
                    // For POS invoices, show individual items
                    if (voucherData.voucher_type === 'POS INVOICE' && invoiceDetails.other_charges) {
                        try {
                            const otherCharges = typeof invoiceDetails.other_charges === 'string'
                                ? JSON.parse(invoiceDetails.other_charges)
                                : invoiceDetails.other_charges;

                            if (otherCharges?.items && Array.isArray(otherCharges.items)) {
                                return otherCharges.items.map((item, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', margin: '4px 0' }}>
                                        <span>{item.product_name || 'Item'} (Qty: {item.quantity})</span>
                                        <span>{formatToInternational(item.subtotal || 0)}</span>
                                    </div>
                                ));
                            }
                        } catch (e) {
                            console.error('Error parsing POS items:', e);
                        }
                    }

                    // Default: show fee breakdown
                    return [
                        { label: 'Registration Fee', val: voucherData.registration_fee },
                        { label: 'Membership Fee', val: voucherData.membership_fee },
                        { label: 'Trainer Fee', val: voucherData.trainer_fee },
                        { label: 'Facilities Fee', val: voucherData.facilities_fee },
                        { label: voucherData.other_charges_name || 'Other Charges', val: voucherData.other_charges_amount }
                    ].filter(item => Number(item.val) > 0).map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', margin: '4px 0' }}>
                            <span>{item.label}</span>
                            <span>{formatToInternational(item.val)}</span>
                        </div>
                    ));
                })()}
            </div>

            {/* Summary Block */}
            <div style={{
                marginTop: 'auto',
                paddingTop: '5px',
                borderTop: '2px solid black'
            }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <div style={{ width: '70%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', margin: '2px 0' }}>
                            <span style={{ fontWeight: 'bold' }}>Total:</span>
                            <span style={{ fontWeight: 'bold' }}>{formatToInternational(totalFee)}</span>
                        </div>

                        {discount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', margin: '2px 0', color: 'red' }}>
                                <span>Discount:</span>
                                <span>-{formatToInternational(discount)}</span>
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '13px',
                            margin: '4px 0',
                            fontWeight: 'bold',
                            borderTop: '1px solid black',
                            borderBottom: '3px double black',
                            padding: '2px 0'
                        }}>
                            <span>Grand Total:</span>
                            <span>{formatToInternational(grandTotal)}</span>
                        </div>

                        {isPaid && (
                            <div style={{
                                textAlign: 'right',
                                color: '#15803d',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                fontStyle: 'italic',
                                marginTop: '4px'
                            }}>
                                FULL PAYMENT RECEIVED
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Notes Section */}
            <div style={{ marginTop: '10px', fontSize: '10px', backgroundColor: '#f9f9f9', padding: '5px', borderRadius: '3px' }}>
                <p style={{ fontWeight: 'bold', marginBottom: '2px' }}>Note:</p>
                <p style={{ margin: '1px 0', fontSize: '9px' }}>• Payment only through Allied Bank, Bank Al-Habib, Bank Islami or Kuickpay.</p>
                <p style={{ margin: '1px 0', fontSize: '9px' }}>• Valid for this membership period only. Non-transferable.</p>
                <p style={{ margin: '1px 0', fontSize: '9px' }}>• Please pay within due date to avoid deactivation.</p>
            </div>

            {/* Stamps/Signatures */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '15px',
                fontSize: '11px',
                fontWeight: 'bold',
                paddingTop: '20px'
            }}>
                <div style={{ textAlign: 'center', width: '40%', borderTop: '1px solid black' }}>Bank Stamp</div>
                <div style={{ textAlign: 'center', width: '40%', borderTop: '1px solid black' }}>Accountant</div>
            </div>

            {/* Print Timestamps Footer */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '10px',
                fontSize: '9px',
                color: '#444',
                fontStyle: 'italic'
            }}>
                <span>Created: {dayjs().format('DD-MM-YYYY')}</span>
                <span>Printed: {dayjs().format('DD-MM-YYYY HH:mm')}</span>
            </div>
        </div>
    );

    return (
        <div style={{
            position: 'relative',
            backgroundColor: 'white',
            width: '100%',
            height: '100%',
            minHeight: '190mm', // Force minimum height for landscape
            margin: '0',
            padding: '0',
            fontFamily: '"Times New Roman", Times, serif'
        }}>
            {/* Global Style for Landscape Printing and Layout Adjustments */}
            <style>
                {`
                    @media print {
                        @page {
                            size: landscape;
                            margin: 0 !important;
                        }
                        body {
                            background-color: white !important;
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 100vw !important;
                            height: 100vh !important;
                            font-family: "Times New Roman", Times, serif !important;
                        }
                        .print-no-margin {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 100% !important;
                            height: 100vh !important;
                            display: flex !important;
                        }
                    }
                `}
            </style>

            {/* 3 Column Layout */}
            <div className="print-no-margin" style={{
                display: 'flex',
                gap: '0',
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                margin: '0',
                padding: '0'
            }}>
                <VoucherColumn copyType="UNIVERSITY COPY" showBorder={true} />
                <VoucherColumn copyType="BANK COPY" showBorder={true} />
                <VoucherColumn copyType="MEMBER COPY" showBorder={false} />
            </div>
        </div>
    );
};

export default BankVoucherLayout;
